import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { InterviewStatus } from "../../models/interview";
import { useCandidateFeedback } from "../../hooks/useCandidateFeedback";
import { CandidateFeedbackModal } from "./CandidateFeedbackModal";

interface CandidateFeedbackPromptProps {
  invitationToken?: string;
  interviewStatus: InterviewStatus;
}

const MAX_COMMENT_LENGTH = 1000;

export function CandidateFeedbackPrompt({
  invitationToken,
  interviewStatus,
}: CandidateFeedbackPromptProps) {
  const { t } = useTranslation('interview');
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [variant, setVariant] = useState<"form" | "success">("form");
  const [hasJustSubmitted, setHasJustSubmitted] = useState(false);
  const { status, isLoading, error, submitFeedback, isSubmitting } =
    useCandidateFeedback(invitationToken);

  useEffect(() => {
    if (interviewStatus !== InterviewStatus.COMPLETED) {
      setIsModalOpen(false);
      setVariant("form");
      setHasJustSubmitted(false);
      return;
    }

    if (hasJustSubmitted) {
      setIsModalOpen(true);
      setVariant("success");
      return;
    }

    if (
      invitationToken &&
      status &&
      !status.has_submitted &&
      !isLoading &&
      !error
    ) {
      setVariant("form");
      setIsModalOpen(true);
      return;
    }

    setIsModalOpen(false);
  }, [
    interviewStatus,
    invitationToken,
    status,
    isLoading,
    error,
    hasJustSubmitted,
  ]);

  useEffect(() => {
    if (variant === "form" && isModalOpen) {
      setRating(null);
      setComment("");
    }
  }, [variant, isModalOpen]);

  const handleSubmit = async () => {
    if (!rating) {
      toast.error(t('feedback.rating_required'));
      return;
    }
    try {
      await submitFeedback({ rating, comment: comment.trim() || undefined });
      setVariant("success");
      setHasJustSubmitted(true);
      setRating(null);
      setComment("");
    } catch (submissionError) {
      const message =
        submissionError instanceof Error
          ? submissionError.message
          : t('feedback.submit_error');
      toast.error(message);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setVariant("form");
    setHasJustSubmitted(false);
    setRating(null);
    setComment("");
  };

  if (!isModalOpen) {
    return null;
  }

  return (
    <CandidateFeedbackModal
      isOpen={isModalOpen}
      variant={variant}
      rating={rating}
      comment={comment}
      candidate=""
      isSubmitting={isSubmitting}
      maxCommentLength={MAX_COMMENT_LENGTH}
      onRatingChange={setRating}
      onCommentChange={setComment}
      onSubmit={handleSubmit}
      onClose={handleClose}
    />
  );
}

